import React from "react";
import { RatingDial } from "../../../RatingDial";
import { RatingDialErrorBoundary } from "../../../RatingDial/RatingDialErrorBoundary";
import { PerformanceBadge } from "newui/features/MySuppliers/components/PerformanceBadge";
import { PreviousRatingIndicator } from "./PreviousRatingIndicator";
import type { SubCriterion } from "newui/features/MySuppliers/types/supplier-performance.types";

interface SubCriterionRowProps {
	subCriterion: SubCriterion;
	score: number | null;
	comment: string | null;
	previousScore?: number | null;
	onScoreChange: (subCriterionId: number, score: number) => void;
	onCommentChange: (subCriterionId: number, comment: string) => void;
}

export const SubCriterionRow: React.FC<SubCriterionRowProps> = ({
	subCriterion,
	score,
	comment,
	previousScore,
	onScoreChange,
	onCommentChange,
}) => {
	return (
		<div
			className="rounded-lg border border-gray-200 p-4"
			data-testid={`sub-criterion-row-${subCriterion.id}`}
		>
			<div className="mb-3 flex items-start justify-between">
				<div className="flex-1">
					<h4 className="text-sm font-medium text-gray-900">
						{subCriterion.name}
					</h4>
					{subCriterion.description && (
						<p className="mt-0.5 text-xs text-gray-500">
							{subCriterion.description}
						</p>
					)}
				</div>
				<div className="ml-4 flex items-center gap-2">
					<PerformanceBadge score={score} size="sm" />
					{previousScore !== null &&
						previousScore !== undefined &&
						score !== null && (
							<PreviousRatingIndicator
								previousScore={previousScore}
								currentScore={score}
							/>
						)}
				</div>
			</div>

			<div className="mb-3">
				<RatingDialErrorBoundary
					value={score}
					onChange={(newScore) =>
						onScoreChange(subCriterion.id, newScore)
					}
				>
					<RatingDial
						value={score}
						onChange={(newScore) =>
							onScoreChange(subCriterion.id, newScore)
						}
						size="sm"
						showLabel={false}
						previousValue={previousScore}
					/>
				</RatingDialErrorBoundary>
			</div>

			<textarea
				value={comment ?? ""}
				onChange={(e) =>
					onCommentChange(subCriterion.id, e.target.value)
				}
				placeholder="Add a comment (optional)..."
				rows={2}
				className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
				data-testid={`comment-${subCriterion.id}`}
			/>
		</div>
	);
};
